// src/pages/about/page.tsx

import React from "react";
import { features, Feature } from "../../constants/utils"; // Fixed path: ../../constants/utils
import strings from "../../constants/strings.json"; // Fixed path: ../../constants/strings.json

interface AboutPageProps {
    locale?: string;
}

interface LocalizedFeature extends Feature {
    title: string;
    description: string;
}

export default function AboutPage({ locale = "en" }: AboutPageProps) {
    const localeStrings: any = strings;
    const about = localeStrings[locale]?.about ?? localeStrings["en"].about;

    const localizedFeatures: LocalizedFeature[] = features.map((f: Feature, i: number) => ({
        ...f,
        title: about[`feature_${i + 1}_title`] ?? f.title,
        description: about[`feature_${i + 1}_description`] ?? f.description,
    }));

    const gradientColors = [
        "from-blue-400 to-cyan-400",
        "from-purple-400 to-pink-400",
        "from-orange-400 to-red-400",
        "from-green-400 to-emerald-400",
        "from-indigo-400 to-blue-400",
        "from-yellow-400 to-orange-400",
    ];

    return (
        <section id="about" className="relative bg-white py-14 md:py-16">
            <div className="max-w-6xl mx-auto px-4">
                <div className="text-center mb-10 max-w-2xl mx-auto">
                    <span className="inline-block px-3 py-1 text-[10px] font-bold tracking-widest uppercase rounded-full bg-gradient-to-r from-[#174143] to-[#3d9ea4] text-white mb-3 shadow-md">
                        {about.section_title}
                    </span>
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                        {about.subtitle}
                    </h2>
                    <p className="text-sm text-gray-600">
                        {about.description}
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
                    {localizedFeatures.map((feature, index) => (
                        <div
                            key={index}
                            className="group relative bg-white rounded-2xl p-5 border border-gray-100 hover:border-transparent hover:shadow-2xl transition-all duration-500 overflow-hidden hover:-translate-y-2"
                        >
                            <div
                                className={`absolute inset-0 bg-gradient-to-br ${gradientColors[index % gradientColors.length]} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}
                            />
                            <div
                                className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${gradientColors[index % gradientColors.length]} scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left rounded-t-2xl`}
                            />
                            <div className="relative z-10">
                                <div className="relative mb-4">
                                    <div
                                        className={`w-12 h-12 bg-gradient-to-br ${gradientColors[index % gradientColors.length]} rounded-xl flex items-center justify-center shadow-md group-hover:shadow-xl group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}
                                    >
                                        <img
                                            src={feature.icon}
                                            alt=""
                                            className="w-6 h-6 object-contain brightness-0 invert group-hover:scale-110 transition-transform duration-500"
                                        />
                                    </div>
                                    <div
                                        className={`absolute inset-0 bg-gradient-to-br ${gradientColors[index % gradientColors.length]} rounded-xl blur-xl opacity-0 group-hover:opacity-40 transition-opacity duration-500 animate-pulse`}
                                    />
                                </div>
                                <h3 className="text-base font-bold text-gray-900 mb-1.5 group-hover:text-[#174143] transition-colors duration-300">
                                    {feature.title}
                                </h3>
                                <p className="text-xs text-gray-600 leading-relaxed line-clamp-2">
                                    {feature.description}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-6 border-t border-gray-100">
                    {about.stats?.map((stat: any, idx: number) => (
                        <div key={idx} className="text-center group cursor-default">
                            <div
                                className={`text-2xl font-bold bg-gradient-to-r ${stat.gradient} bg-clip-text text-transparent mb-0.5 group-hover:scale-110 transition-transform duration-300`}
                            >
                                {stat.value}
                            </div>
                            <div className="text-xs text-gray-600">{stat.label}</div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}